import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import {
  rxResource,
  takeUntilDestroyed,
  toSignal,
} from '@angular/core/rxjs-interop';
import {
  form,
  FormField,
  required,
  validateTree,
} from '@angular/forms/signals';
import { ActivatedRoute } from '@angular/router';
import { KENDO_INDICATORS } from '@progress/kendo-angular-indicators';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import {
  InternalAuthApiService,
  PasswordChangeResult,
  PasswordResetType,
} from '@ups/xplat/base/auth';
import { finalize, map, of } from 'rxjs';
import { isSafeReturnTo } from './safe-return-to';

/**
 * Public InternalAuth set-password / reset landing page. Reached via the emailed
 * reset link (forgot-password) or the InitialSet onboarding-activation link
 * ({@link code} is the reset token). Shared across every app that uses InternalAuth
 * — host apps route an unauthenticated path to it (e.g. `public/password-reset/:code`).
 *
 * Self-contained: talks only to the Software Security InternalAuth API through the
 * root {@link InternalAuthApiService} (which resolves its base URL from
 * `environment.security.apiUrl`), so no host-app InternalAuth login/session wiring
 * is required — just knowledge of the SS API URL.
 */
@Component({
  selector: 'ups-password-reset',
  templateUrl: 'password-reset.component.html',
  styleUrl: 'password-reset.component.css',
  imports: [FormField, KENDO_INDICATORS, ButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordResetComponent {
  route = inject(ActivatedRoute);
  internalAuthApi = inject(InternalAuthApiService);
  private destroyRef = inject(DestroyRef);
  private redirectTimeout?: ReturnType<typeof setTimeout>;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.redirectTimeout) {
        clearTimeout(this.redirectTimeout);
      }
    });
  }

  code = toSignal(this.route.paramMap.pipe(map((x) => x.get('code'))), {
    requireSync: true,
  });

  info = rxResource({
    params: () => ({ code: this.code() }),
    stream: ({ params: { code } }) =>
      code ? this.internalAuthApi.passwordResetInfo({ code }) : of(null),
  });

  // The page doubles as the InitialSet ("set your password" for a new employee)
  // and the forgot-password reset landing; branch the copy off the reset type.
  isInitialSet = computed(
    () => this.info.value()?.type === PasswordResetType.InitialSet
  );

  passwordResetModel = signal({
    newPassword: '',
    verifyPassword: '',
  });

  passwordResetForm = form(this.passwordResetModel, (x) => {
    required(x.newPassword);
    required(x.verifyPassword);
    validateTree(x, (y) =>
      y.value().newPassword === y.value().verifyPassword
        ? null
        : { kind: 'pass-not-equal', message: "The passwords don't match" }
    );
  });

  passwordChangeResult = PasswordChangeResult;
  isLoading = signal(false);
  submitResult = signal<null | 'internal-error' | PasswordChangeResult>(null);
  // Only offer/perform a redirect when the returnTo is present AND host-allowed.
  willRedirectBack = computed(() =>
    isSafeReturnTo(this.info.value()?.returnTo)
  );

  submit(event: Event) {
    event.preventDefault();

    if (!this.passwordResetForm().valid() || this.isLoading()) {
      return;
    }

    this.submitResult.set(null);
    this.isLoading.set(true);
    this.internalAuthApi
      .resetPassword({
        code: this.code() as string,
        password: this.passwordResetModel().newPassword,
      })
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (x) => {
          this.submitResult.set(x.result);
          // Only leave the page once the password was actually accepted — a
          // WeakPassword result is HTTP 200 but must leave the form up to retry —
          // and only to an allow-listed returnTo host (see isSafeReturnTo).
          if (
            x.result === PasswordChangeResult.Success &&
            isSafeReturnTo(this.info.value()?.returnTo)
          ) {
            this.tryRedirect();
          }
        },
        error: () => {
          this.submitResult.set('internal-error');
        },
      });
  }

  tryRedirect() {
    const url = this.info.value()?.returnTo;
    if (!isSafeReturnTo(url)) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.redirectTimeout = setTimeout(() => (location.href = url!), 5_000);
  }
}
